import React from 'react'
import {noop} from 'lodash'
import classNames from 'classnames'
import SVG from 'react-inlinesvg'
// @ts-ignore
import Slider from 'rc-slider'
import playSrc from 'player/assets/play.svg'
import pauseSrc from 'player/assets/pause.svg'
import restartSrc from 'player/assets/restart.svg'
import fullScreenSrc from 'player/assets/fullscreen.svg'
import exitFullScreenSrc from 'player/assets/exit-fullscreen.svg'
import brandLogoBigSrc from 'player/assets/brand-powtoon-big.svg'
import brandLogoSrc from 'player/assets/brand-powtoon.svg'
import volSrc from 'player/assets/vol.svg'
import volMutedSrc from 'player/assets/vol-muted.svg'
import volBigSrc from 'player/assets/vol-big.svg'
import volMutedBigSrc from 'player/assets/vol-muted-big.svg'
import './PlayerVideoControls.scss'

interface PlayerVideoControlsProps {
  totalSeconds: number,
  progress: number,
  isPlaying?: boolean,
  isFullScreen?: boolean,
  volume?: number,
  showRestartAtEnd?: boolean,
  onPlayToggle?: (isPause: boolean) => void,
  onFullScreenToggle?: () => void,
  onScrollbarChange?: (percent: number) => void,
  onAfterScrollbarChange?: (percent: number) => void,
  onVolumeChange?: (percent: number) => void
}
const STEPS = 100
const SLIDER_STEPS_RATIO = 1 / STEPS
const paddedFloor = (num: number) => Math.floor(num).toString().padStart(2, '0')
const formatSeconds = (seconds: number) => `${Math.floor(seconds / 60)}:${paddedFloor(seconds % 60)}`

const PlayerVideoControls: React.FC<PlayerVideoControlsProps> = ({
  isPlaying = false,
  isFullScreen = false,
  volume = 1,
  totalSeconds,
  progress,
  showRestartAtEnd = false,
  onScrollbarChange,
  onAfterScrollbarChange,
  onPlayToggle = noop,
  onFullScreenToggle = noop,
  onVolumeChange = noop
}) => {
  const [lastVolume, setLastVolume] = React.useState(1)
  const showingRestart: boolean = React.useMemo(() => showRestartAtEnd &&
    Math.round(progress) >= Math.round(totalSeconds) &&
    totalSeconds > 0 &&
    !isPlaying,
  [showRestartAtEnd, progress, totalSeconds, !isPlaying])
  const onPlayButtonClicked = React.useCallback(() => onPlayToggle(isPlaying), [isPlaying, onPlayToggle])
  const onFullScreenToggleClicked = React.useCallback(
    () => onFullScreenToggle(isFullScreen),
    [isFullScreen, onFullScreenToggle]
  )
  const time = React.useMemo(() => `${formatSeconds(progress)}/${formatSeconds(totalSeconds)}`,
    [totalSeconds, progress])
  const progressBarStyle = React.useMemo(() => {
    const clampedProgress = Math.min(Math.max(progress, 0), totalSeconds)
    return {left: `${clampedProgress * 100 / totalSeconds}%`}
  }, [totalSeconds, progress])
  const onVolumeSliderChange = React.useCallback((newVolume) => {
    setLastVolume(volume)
    onVolumeChange(newVolume)
  }, [onVolumeChange, volume])
  const onMuteToggleClicked = React.useCallback(() => {
    if (volume === 0) {
      onVolumeSliderChange(lastVolume === 0 ? 1 : lastVolume)
    }
    else {
      onVolumeSliderChange(0)
    }
  }, [onVolumeSliderChange, lastVolume])
  const volumeSrc = React.useMemo(() => {
    if (volume === 0) {
      return isFullScreen ? volMutedBigSrc : volMutedSrc
    }
    return isFullScreen ? volBigSrc : volSrc
  }, [isFullScreen, volume])

  const playToggleSrc = React.useMemo(() => {
    if (showingRestart) {
      return restartSrc
    }
    else {
      return isPlaying ? pauseSrc : playSrc
    }
  }, [isPlaying, showingRestart])

  return (
    <div styleName="container">
      <div styleName="background" />
      <div styleName="controls">
        <div styleName="play-toggle-container">
          <button styleName="play-toggle" onClick={onPlayButtonClicked}>
            <SVG
              src={playToggleSrc}
              styleName={classNames('play-toggle-icon', {restart: showingRestart})}
            />
          </button>
        </div>
        <div styleName="progress-bar-container">
          <Slider
            min={0}
            max={Math.floor(totalSeconds * STEPS) / STEPS}
            step={SLIDER_STEPS_RATIO}
            value={Math.floor(progress * STEPS) / STEPS}
            onChange={onScrollbarChange}
            onAfterChange={onAfterScrollbarChange}
          />
        </div>
        <div styleName="time-container">
          <span styleName="time">{time}</span>
        </div>
        <div styleName="brand-container">
          <a href="https://www.powtoon.com" target="_blank" styleName="brand-powtoon">
            <img styleName="logo" src={isFullScreen ? brandLogoBigSrc : brandLogoSrc} />
          </a>
        </div>
        <div styleName="sound-control-container">
          <button styleName="sound-control" onClick={onMuteToggleClicked}>
            <SVG src={volumeSrc} styleName="sound-control-icon" />
          </button>
          <div styleName="sound-bar-container">
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={onVolumeSliderChange}
              vertical={true}
            />
          </div>
        </div>
        <div styleName="full-screen-container">
          <button styleName="full-screen-toggle" onClick={onFullScreenToggleClicked}>
            <SVG src={isFullScreen ? exitFullScreenSrc : fullScreenSrc} styleName="full-screen-icon" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default PlayerVideoControls